import { Button } from "@9amhealth/shared";
import styled from "@emotion/styled";
import { FC } from "react";
import { APP_BREAKPOINT, APP_CONTENT_WIDTH } from "src/constants/layout";
import { getDefaultRxDietData, RxDietData } from "src/constants/rxDietData";
import rxDietCardBgImgDesktop from "src/ui/assets/images/rxdiet-card-bg-desktop.jpg";
import rxDietCardBgImgMobile from "src/ui/assets/images/rxdiet-card-bg-mobile.jpg";
import { DisableAppPadding } from "../AppRouter/AppRouter";

const PageBackground = styled.div`
  display: flex;
  flex-direction: column;
  flex: 1;
  background-image: url(${rxDietCardBgImgMobile});
  background-position: center top;
  background-repeat: no-repeat;
  background-size: cover;

  @media screen and (min-width: ${APP_BREAKPOINT}px) {
    justify-content: center;
    background-image: url(${rxDietCardBgImgDesktop});
  }
`;

const ContentContainer = styled.div`
  display: flex;
  flex-direction: column;
  gap: 1.25rem;
  padding: 5rem 1.5rem 2rem;
  color: var(--color-charcoal);
  width: 100%;
  max-width: ${APP_CONTENT_WIDTH}px;
  margin: 0 auto;

  @media screen and (min-width: ${APP_BREAKPOINT}px) {
    gap: 1.5rem;
    padding: 6rem 1.5rem 2.5rem;
  }
`;

const StyledList = styled.ul`
  margin: 0;
`;

const StyledListItem = styled.li`
  margin: 0 0 0.625rem 0;
  padding: 0;
  display: flex;
  column-gap: 0.5rem;

  &::before {
    display: none;
  }
`;

export const StyledPill = styled.div`
  color: var(--Partners-RxDiet-Primary, #6235cc);
  border-radius: 7.5rem;
  background: rgba(98, 53, 204, 0.08);
  backdrop-filter: blur(0.5rem);
  width: fit-content;
  padding: 0.125rem 0.5rem;
  display: flex;
  align-items: center;
  gap: 0.21875rem;
`;

const ImgCarouselWrapper = styled.div`
  mask-image: linear-gradient(
    to right,
    transparent 0%,
    black 10%,
    black 90%,
    transparent 100%
  );
  -webkit-mask-image: linear-gradient(
    to right,
    transparent 0%,
    black 10%,
    black 90%,
    transparent 100%
  );
`;

const ImgCarousel = styled.div`
  display: flex;
  gap: 0.75rem;
  margin-top: 2.5rem;
  padding-bottom: 2.5rem;

  // Scrollbar
  overflow-x: scroll;
  scrollbar-width: none;
  -ms-overflow-style: none;
  &::-webkit-scrollbar {
    display: none;
  }

  img {
    width: 5rem;
    aspect-ratio: 1;
    position: relative;
    border-radius: 50%;
    box-shadow:
      -13px 11px 17px 0px rgba(98, 53, 204, 0.06),
      -3px 3px 9px 0px
        color-mix(in srgb, var(--color-charcoal, #212121) 20%, transparent);
  }
`;

interface RxDietProps {
  data?: RxDietData;
  onContinue?: () => void;
}

const RxDietFirstScreen: FC<RxDietProps> = ({
  data = getDefaultRxDietData(),
  onContinue
}) => {
  const { card } = data;

  return (
    <>
      <PageBackground>
        <DisableAppPadding />
        <ContentContainer>
          {card.pillText && (
            <StyledPill>
              <small className="as-tiny">{card.pillText}</small>
              {card.pillImg}
            </StyledPill>
          )}

          {/* eslint-disable-next-line @typescript-eslint/prefer-nullish-coalescing */}
          {(card.title || card.subtitle) && (
            <div>
              {card.title && (
                <>
                  <h4 className="as-h4-large">{card.title}</h4>
                  <nine-spacer s="xxs" />
                </>
              )}
              {card.subtitle && <p className="m0">{card.subtitle}</p>}
            </div>
          )}

          {Boolean(card.listItems?.length) && (
            <StyledList>
              {card.listItems?.map((listItem, i) => (
                <StyledListItem key={`rxdiet-list-item-${i}`}>
                  <div>{listItem.icon}</div>
                  <small>{listItem.text}</small>
                </StyledListItem>
              ))}
            </StyledList>
          )}

          <div>
            {Boolean(card.sliderImgs?.length) && (
              <ImgCarouselWrapper>
                <ImgCarousel>
                  {card.sliderImgs?.map((img, i) => (
                    <img
                      key={`rxdiet-slider-img-${i}`}
                      src={img.src}
                      alt=""
                      aria-hidden="true"
                    />
                  ))}
                </ImgCarousel>
              </ImgCarouselWrapper>
            )}

            {!card.sliderImgs?.length && <nine-spacer s="xs" />}

            <Button
              onPress={() => {
                onContinue?.();
              }}
              theme="rxdiet"
              style={{ width: "100%" }}
            >
              {card.buttonText}
            </Button>
          </div>
        </ContentContainer>
      </PageBackground>
    </>
  );
};

export default RxDietFirstScreen;
